<template>
  <div id="app" :class="pageLayout">

    <loading-spinner v-if="!teamsiteConfig && !httpError"></loading-spinner>
    <error-page v-if="httpError" :error="httpError"></error-page>

    <teamsite-header
        :pageLayout="pageLayout"
        v-if="teamsiteConfig"
        rootPath="/"
        :buttonColor="teamsiteConfig.color"
        :login="{ name: 'Login', url: routes.signInUrl }"
        :contact="{ text: signupBtnText, hint: 'Starten Sie jetzt!', url: routes.signUpUrl }"
        :navItems="navItems"
      ></teamsite-header>

    <debug-switch v-if="previewMode"
        :layout="teamToken"
        :backendEnv="backendEnv"
        @selectBackendEnv="backendEnv = $event"
        @selectTeam="teamToken = $event"
      ></debug-switch>

    <router-view/>

    <teamsite-footer
      v-if="appLogo"
      :logoUrl="appLogo"
    ></teamsite-footer>

    <viewport-detection></viewport-detection>

  </div>
</template>

<script>
import {MoqoApi} from '@/lib/MoqoApi.js'
import InjectedStyles from '@/lib/injected_styles.js'

export default {
  name: 'App',

  created () {
    MoqoApi.setBackendEnv(this.backendEnv)
    this.teamToken = this.defaultTeamToken
    this.applyCanonicalUrl(this.teamToken)
  },

  watch: {
    backendEnv() {
      MoqoApi.setBackendEnv(this.backendEnv)
      MoqoApi.refreshEcpConfig(this.teamToken)
    },

    teamToken() {
      MoqoApi.refreshEcpConfig(this.teamToken)
    },

    teamsiteConfig () {
      InjectedStyles.apply(this.teamsiteConfig.color, this.moqoColor)
      if (this.teamsiteConfig.favicon) {
        this.applyFavicon(this.teamsiteConfig.favicon.medium_url || this.teamsiteConfig.favicon.url)
      } else if(this.teamsiteConfig.navigation_logo || this.teamsiteConfig.logo) {
        const logo = this.teamsiteConfig.navigation_logo || this.teamsiteConfig.logo
        this.applyFavicon(logo.medium_url || logo.url)
      } else {
        this.applyFavicon()
      }


      let title = this.teamsiteConfig.title || this.teamsiteConfig.name
      if (this.teamsiteConfig.subtitle) {
        title += ` - ${this.teamsiteConfig.subtitle}`
      }
      document.title = title
    }
  },

  methods: {
    applyFavicon (url) {
      let link = document.querySelector("link[rel*='icon']") || document.createElement('link')
      link.type = 'image/x-icon'
      link.rel = 'icon'
      if (url) {
        link.href = url
      } else {
        // blank inline favicon if no url is given
        link.href = 'data:image/x-icon;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQEAYAAABPYyMiAAAABmJLR0T///////8JWPfcAAAACXBIWXMAAABIAAAASABGyWs+AAAAF0lEQVRIx2NgGAWjYBSMglEwCkbBSAcACBAAAeaR9cIAAAAASUVORK5CYII='
      }
      document.head.appendChild(link)
    },
    applyCanonicalUrl (token) {
      if (!token) { return }

      let link = document.querySelector("link[rel*='canonical']") || document.createElement('link')
      link.rel = 'canonical'
      link.href = 'https://' + token + '.moqo.de'
      document.head.appendChild(link)
    }
  },

  data() {
    return {
      teamToken: null,
      backendEnv: localStorage.getItem('backendEnv') || 'release'
    }
  },

  computed : {

    defaultTeamToken () {
      if (this.previewMode) {
        return localStorage.getItem('teamToken')
      } else {
        let parts = window.location.host.split('.')
        if (parts.length >= 3) {
          return parts[0]
        } else {
          // No actual subdomain; use "www" as a placeholder. We would have to handle these cases the same way anyway
          // The backend will then return a configuration that matches the referer.
          return 'www'
        }
      }
      return localStorage.getItem('teamToken')
    },

    appLogo () {
      if (!this.teamsiteConfig) return

      if (this.teamsiteConfig.app && this.teamsiteConfig.app.icon) {
        return this.teamsiteConfig.app.icon.url
      }

      if (this.teamsiteConfig.logo || this.teamsiteConfig.navigation_logo) {
        return (this.teamsiteConfig.logo || this.teamsiteConfig.navigation_logo).url
      }

      return this.teamsiteConfig.image.url
    },

    navItems () {
      if (this.teamsiteConfig && this.teamsiteConfig.cars) {

        let isFreeSuffix = this.isFreeForAll ? '_free' : ''
        return [{ name: this.$t(`navigation.${this.vehicleString(`cars${isFreeSuffix}`, `bikes${isFreeSuffix}`)}`), path: 'cars' }]
      }
      return []
    },

    pageLayout () {
      return {
        teamsite: 'hero'
      }[this.$route.name] || 'default'
    },

    isProduction () {
      return process.env.NODE_ENV == 'production'
    }
  }

}
</script>

<style lang="scss">
@font-face {
  font-family: 'CeraPro';
  src:
    url('./assets/fonts/CeraPro-Regular.eot') format('embedded-opentype'),
    url('./assets/fonts/CeraPro-Regular.woff') format('woff'),
    url('./assets/fonts/CeraPro-Regular.woff2') format('woff2');
  font-style: normal;
  font-weight: 400;
}

@font-face {
  font-family: 'CeraPro';
  src:
    url('./assets/fonts/CeraPro-Medium.eot') format('embedded-opentype'),
    url('./assets/fonts/CeraPro-Medium.woff') format('woff'),
    url('./assets/fonts/CeraPro-Medium.woff2') format('woff2');
  font-style: normal;
  font-weight: bold;
}

html, body, #app {
  min-height: 100%;
}

#app {
  padding-top: rem-calc(150);
  background-color: #E2E2E2;
  @include breakpoint(medium down) {
    padding-top: rem-calc(100);
  }
  @include breakpoint(small only) {
    padding-top: rem-calc(100);
  }
  &.hero {
    padding-top: 0;
  }
}

header .menu a {
  text-shadow: 3px 3px 8px #505050, 3px -3px 8px #505050, -3px -3px 8px #505050, -3px 3px 8px #505050;
  text-shadow: 3px 3px 8px rgba(40, 40, 40, .5), 3px -3px 8px rgba(40, 40, 40, .5), -3px -3px 8px rgba(40, 40, 40, .5), -3px 3px 8px rgba(40, 40, 40, .5);
}
header.header--with-bg .menu a {
  text-shadow: none;
}

#hero h1, #hero p {
  text-shadow: 2px 2px 20px #353535, 2px -2px 20px #353535, -2px -2px 20px #353535, -2px 2px 20px #353535;
  text-shadow: 2px 2px 20px rgba(53, 53, 53, 0.25), 2px -2px 20px rgba(53, 53, 53, 0.25), -2px -2px 20px rgba(53, 53, 53, 0.25), -2px 2px 20px rgba(53, 53, 53, 0.25);
}

.desktop-navigation ul.navigation li {
  margin-left: 1.5rem;
}

</style>



// WEBPACK FOOTER //
// src/App.vue